import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Circle, Clock, Lightbulb, ArrowRight, ArrowLeft, ClipboardCheck } from "lucide-react";
import { authHeaders, formatApiErrorDetail } from "@/components/schulungen/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Inhalt({ block }) {
  if (block.typ === "text") return <p className="text-slate-700 leading-relaxed">{block.text}</p>;
  if (block.typ === "liste") return (
    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
      {block.titel && <p className="font-semibold text-slate-800 mb-3">{block.titel}</p>}
      <ul className="space-y-2">{block.punkte.map((p, i) => <li key={i} className="flex items-start gap-2.5 text-slate-700"><CheckCircle2 className="h-5 w-5 text-cyan-700 shrink-0 mt-0.5" />{p}</li>)}</ul>
    </div>
  );
  if (block.typ === "merke") return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3" data-testid="merke-box">
      <Lightbulb className="h-6 w-6 text-amber-600 shrink-0" />
      <div><p className="text-xs font-bold tracking-wider uppercase text-amber-700 mb-1">Merke</p><p className="text-slate-800 leading-relaxed">{block.text}</p></div>
    </div>
  );
  return null;
}

export default function ModulView() {
  const { modulId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/schulungen/kurs`, authHeaders(user)).then((r) => setData(r.data)).catch(() => toast.error("Kurs konnte nicht geladen werden"));
  }, [user]);

  useEffect(() => { window.scrollTo(0, 0); }, [modulId, params]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>;

  const module = data.kurs.module;
  const mIdx = module.findIndex((m) => m.id === modulId);
  if (mIdx < 0) return <p className="text-slate-500">Modul nicht gefunden.</p>;
  const modul = module[mIdx];
  const lektionId = params.get("lektion") || modul.lektionen[0].id;
  const lIdx = Math.max(0, modul.lektionen.findIndex((l) => l.id === lektionId));
  const lektion = modul.lektionen[lIdx];
  const done = !!data.fortschritt[lektion.id];
  const nextLektion = modul.lektionen[lIdx + 1];
  const nextModul = module[mIdx + 1];
  const allDone = data.summary.test_freigeschaltet;

  const goNext = () => {
    if (nextLektion) setParams({ lektion: nextLektion.id });
    else if (nextModul) navigate(`/schulungen/teilnehmer/modul/${nextModul.id}`);
    else navigate("/schulungen/teilnehmer/test");
  };

  const complete = async () => {
    setSaving(true);
    try {
      const { data: r } = await axios.post(`${API}/schulungen/kurs/lektionen/${lektion.id}/abschliessen`, {}, authHeaders(user));
      setData((d) => ({ ...d, fortschritt: r.fortschritt, summary: r.summary }));
      if (r.summary.test_freigeschaltet && !nextLektion && !nextModul) toast.success("Alle Lektionen abgeschlossen – der Abschlusstest ist freigeschaltet!");
      else toast.success("Lektion abgeschlossen");
      goNext();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="modul-view">
      <aside className="lg:col-span-4 space-y-4">
        <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
          <p className="text-xs font-semibold tracking-wider uppercase text-cyan-700">Modul {modul.nr} · {modul.ustd} UStd</p>
          <h1 className="font-['Manrope'] text-lg font-bold text-slate-900 mt-1" data-testid="modul-titel">{modul.titel}</h1>
          <ol className="mt-4 space-y-1">
            {modul.lektionen.map((l, i) => {
              const isActive = l.id === lektion.id;
              return (
                <li key={l.id}>
                  <button onClick={() => setParams({ lektion: l.id })} data-testid={`lektion-nav-${l.id}`}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-cyan-50 text-cyan-800 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
                    {data.fortschritt[l.id] ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
                    <span className="flex-1">{i + 1}. {l.titel}</span>
                    <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{l.dauer_min} min</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
        <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
          <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3">Alle Module</p>
          <div className="space-y-1">
            {module.map((m) => {
              const d = m.lektionen.filter((l) => data.fortschritt[l.id]).length;
              return <Link key={m.id} to={`/schulungen/teilnehmer/modul/${m.id}`} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${m.id === modul.id ? "bg-slate-100 font-semibold text-slate-800" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`modul-nav-${m.id}`}><span>Modul {m.nr}</span><span className="text-xs text-slate-400">{d}/{m.lektionen.length}</span></Link>;
            })}
          </div>
          {allDone && <Link to="/schulungen/teilnehmer/test" className="mt-4 flex items-center justify-center gap-2 h-11 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold" data-testid="zum-test-btn"><ClipboardCheck className="h-4 w-4" />Zum Abschlusstest</Link>}
        </div>
      </aside>

      <article className="lg:col-span-8">
        <div className="bg-white border border-slate-200/60 rounded-2xl p-6 sm:p-10">
          <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Lektion {lIdx + 1} von {modul.lektionen.length} · ca. {lektion.dauer_min} Minuten</p>
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mt-2" data-testid="lektion-titel">{lektion.titel}</h2>
          <div className="mt-8 space-y-6 text-base">{lektion.inhalt.map((b, i) => <Inhalt key={i} block={b} />)}</div>
          <div className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <button onClick={() => lIdx > 0 ? setParams({ lektion: modul.lektionen[lIdx - 1].id }) : navigate("/schulungen/teilnehmer")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="lektion-zurueck"><ArrowLeft className="h-4 w-4" />{lIdx > 0 ? "Vorherige Lektion" : "Zur Übersicht"}</button>
            {done ? (
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700" data-testid="lektion-done-badge"><CheckCircle2 className="h-5 w-5" />Abgeschlossen</span>
                <Button onClick={goNext} variant="outline" className="rounded-xl h-11" data-testid="lektion-weiter">{nextLektion || nextModul ? "Weiter" : "Zum Test"}<ArrowRight className="ml-2 h-4 w-4" /></Button>
              </div>
            ) : (
              <Button onClick={complete} disabled={saving} className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 px-6 font-semibold" data-testid="lektion-abschliessen">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Lektion abschließen{(nextLektion || nextModul) ? " & weiter" : ""}<ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}
